import React, { useEffect, useState, useMemo } from 'react';

import withNavigation from "../higher-order/navigation.js";
import Translator from '../translator.js';
import OuiNonToggle from './oui-non-toggle.js';
import PointEauSection from './point-eau-section.js';
import CaptureVerificationSommaire from './capture-verification-sommaire.js';
import FermerEnregistrementFormBase from '../fermer-enregistrement/shared/fermer-enregistrement-form-base.js';
import { useFermerEnregistrementForm } from '../fermer-enregistrement/shared/use-fermer-enregistrement-form.js';

import { useSelector } from "react-redux";

import { PATH_FERMER_ENREGISTREMENT } from '../../constants/navigation-constants.js';
import {
  getContexteFermeturePeche,
  getEspecesPointEau,
} from '../../server/services/fermer-enregistrement-service.js';
import {
  createEmptyPointEauBlock,
  buildCaptureDetailsFromEspeces,
  getPointsEauxDisponibles,
  formEstComplete,
  afficherAutrePointEau,
  shouldRenderPointEauBlock,
  hasMorePointsEauxDisponibles,
  estComplete,
  pruneOrphanPointEauBlocks,
  buildFermetureCapturePayload,
  appliquerAutreEspeceOui,
  appliquerAutreEspeceNon,
  appliquerChangementEspeceActive,
  verrouillerEspeceActiveSiCompletee,
  obtenirEspeceActiveInitiale,
  estQuestionAutreEspeceResolue,
} from '../../utils/fermer-enregistrement-peche-capture.js';

import './style.css';

const FermerEnregistrementEtCapture = ({ goTo, location, language }) => {
  const { fermerEnregistrement: { idEnregistrementEntete } } = useSelector(state => state);
  const TEXTS = useMemo(() => Translator(language, 'TXT_FERMER_ENREGISTREMENT_PAGE'), [language]);

  const [dataLoading, setDataLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [aPeche, setAPeche] = useState(null);
  const [pointsEau, setPointsEau] = useState([]);
  const [allPointsEaux, setAllPointsEaux] = useState([]);
  const [especesParPointEau, setEspecesParPointEau] = useState({});
  const [idActivitePeche, setIdActivitePeche] = useState('');
  const [idEnregistrementDetailPeche, setIdEnregistrementDetailPeche] = useState('');

  const {
    calendarRef,
    date,
    load,
    heureIsSlected,
    dateIsSelected,
    setdateIsSelected,
    isOpenCalendar,
    setIsOpenCalendar,
    enregistrementEntete,
    dateErrorKey,
    minSelectableDate,
    maxSelectableDate,
    authToken,
    handleDateChange,
    handleTimeChange,
    submitFermeture,
  } = useFermerEnregistrementForm({
    location,
    idEnregistrementEntete,
    language,
  });

  useEffect(() => {
    if (!enregistrementEntete) {
      setDataLoading(false);
      return;
    }

    let cancelled = false;

    const loadData = async () => {
      setDataLoading(true);
      setLoadError('');
      try {
        const contexte = await getContexteFermeturePeche(enregistrementEntete);

        if (cancelled) {
          return;
        }

        setIdActivitePeche(contexte.idActivitePeche || '');
        setIdEnregistrementDetailPeche(contexte.idEnregistrementDetailPeche || '');
        setAllPointsEaux(contexte.pointsEau);
      } catch (error) {
        if (!cancelled) {
          console.error(error);
          setLoadError(TEXTS.loadDataError);
        }
      } finally {
        if (!cancelled) {
          setDataLoading(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [enregistrementEntete, TEXTS.loadDataError]);

  const loadEspecesForPointEau = async (idPointEau) => {
    if (!idPointEau) {
      return [];
    }
    if (especesParPointEau[idPointEau]) {
      return especesParPointEau[idPointEau];
    }

    const especes = await getEspecesPointEau(idPointEau);
    setEspecesParPointEau(prev => ({ ...prev, [idPointEau]: especes }));
    return especes;
  };

  const updatePointsEau = (updater) => {
    setPointsEau(prev => pruneOrphanPointEauBlocks(updater(prev), allPointsEaux));
  };

  const handleAPecheChange = (value) => {
    setAPeche(value);
    if (value === true) {
      setPointsEau([createEmptyPointEauBlock()]);
    } else {
      setPointsEau([]);
    }
  };

  const handlePointEauChange = async (index, idPointEau) => {
    const especes = idPointEau ? await loadEspecesForPointEau(idPointEau) : [];
    const captureDetailsTemplate = buildCaptureDetailsFromEspeces(especes);

    updatePointsEau(prev => {
      const updated = prev.slice(0, index + 1).map((block, i) => {
        if (i !== index) {
          return block;
        }
        return {
          ...block,
          idPointEau,
          nombrePersonnes: '',
          nombreJours: '',
          autrePointEau: null,
          autreEspece: null,
          especeRounds: [],
          activeEspeceId: '',
          aEuDesPrises: null,
          capturesDetails: captureDetailsTemplate.map(d => ({ ...d })),
        };
      });
      return updated;
    });
  };

  const handleNombrePersonnesChange = (index, value) => {
    updatePointsEau(prev => {
      const updated = prev.map((block, i) => {
        if (i !== index) {
          return block;
        }
        const nombreJours = Number(value) > 0 ? block.nombreJours : '';
        const countsComplete = Number(value) > 0 && Number(nombreJours) > 0;
        return {
          ...block,
          nombrePersonnes: value,
          nombreJours,
          ...(!countsComplete ? {
            autrePointEau: null,
            autreEspece: null,
            especeRounds: [],
            activeEspeceId: '',
            aEuDesPrises: null,
            capturesDetails: [],
          } : {}),
        };
      });
      if (!estComplete(updated[index])) {
        return updated.slice(0, index + 1);
      }
      return updated;
    });
  };

  const handleNombreJoursChange = (index, value) => {
    updatePointsEau(prev => {
      const updated = prev.map((block, i) => {
        if (i !== index) {
          return block;
        }
        const countsComplete = Number(block.nombrePersonnes) > 0 && Number(value) > 0;
        return {
          ...block,
          nombreJours: value,
          ...(!countsComplete ? {
            autrePointEau: null,
            autreEspece: null,
            especeRounds: [],
            activeEspeceId: '',
            aEuDesPrises: null,
            capturesDetails: [],
          } : {}),
        };
      });
      if (!estComplete(updated[index])) {
        return updated.slice(0, index + 1);
      }
      return updated;
    });
  };

  const handlePrisesChange = async (blockIndex, value) => {
    let captureDetailsTemplate = [];
    const block = pointsEau[blockIndex];

    if (value === true) {
      if (block.capturesDetails?.length > 0) {
        captureDetailsTemplate = block.capturesDetails;
      } else if (block.idPointEau) {
        const especes = await loadEspecesForPointEau(block.idPointEau);
        captureDetailsTemplate = buildCaptureDetailsFromEspeces(especes);
      }
    } else if (value === false && block.idPointEau) {
      // Aucune prise : on garde quand même un détail à 0 pour chaque espèce.
      const especes = await loadEspecesForPointEau(block.idPointEau);
      captureDetailsTemplate = buildCaptureDetailsFromEspeces(especes);
    }

    updatePointsEau(prev => prev.map((blockItem, i) => {
      if (i !== blockIndex) {
        return blockItem;
      }
      return {
        ...blockItem,
        aEuDesPrises: value,
        autreEspece: null,
        autrePointEau: null,
        especeRounds: [],
        activeEspeceId: value === true
          ? obtenirEspeceActiveInitiale(captureDetailsTemplate)
          : '',
        capturesDetails: (value === true || value === false)
          ? captureDetailsTemplate.map(d => ({ ...d }))
          : [],
      };
    }));
  };

  const handleCaptureDetailChange = (blockIndex, detailIndex, field, value, extraUpdates = null) => {
    updatePointsEau(prev => prev.map((block, i) => {
      if (i !== blockIndex) {
        return block;
      }
      const updatedDetails = block.capturesDetails.map((d, di) => {
        if (di !== detailIndex) {
          return d;
        }
        return {
          ...d,
          [field]: value,
          ...(extraUpdates || {}),
        };
      });
      return verrouillerEspeceActiveSiCompletee({ ...block, capturesDetails: updatedDetails });
    }));
  };

  const handleEspeceChange = (index, speciesId) => {
    updatePointsEau(prev => prev.map((block, i) => {
      if (i !== index) {
        return block;
      }
      return appliquerChangementEspeceActive(block, speciesId);
    }));
  };

  const handleAutreEspeceChange = (index, value, roundIndex) => {
    updatePointsEau(prev => prev.map((block, i) => {
      if (i !== index) {
        return block;
      }
      if (value === true) {
        return appliquerAutreEspeceOui(block, roundIndex);
      }
      return appliquerAutreEspeceNon(block, roundIndex);
    }));
  };

  const handleAutrePointEauChange = (index, value) => {
    updatePointsEau(prev => {
      const block = prev[index];
      if (value === true && !estQuestionAutreEspeceResolue(block)) {
        return prev;
      }
      if (value === true) {
        if (!hasMorePointsEauxDisponibles(allPointsEaux, prev)) {
          return prev.map((block, i) =>
            i === index ? { ...block, autrePointEau: false } : block
          );
        }
        const updated = prev.map((block, i) =>
          i === index ? { ...block, autrePointEau: value } : block
        );
        return [...updated, createEmptyPointEauBlock()];
      }

      return prev.slice(0, index + 1).map((block, i) =>
        i === index ? { ...block, autrePointEau: false } : block
      );
    });
  };

  const navigation = async () => {
    if (authToken) {
      const result = await submitFermeture((decodedToken, dateFermetureISO) =>
        buildFermetureCapturePayload({
          idProfile: decodedToken.idProfile,
          dateFermetureISO,
          idEnregistrementEntete: enregistrementEntete,
          idEnregistrementDetailPeche,
          aPeche,
          pointsEau,
        })
      );
      if (result) {
        goTo(PATH_FERMER_ENREGISTREMENT);
      }
    }
  };

  const showFishingQuestion = dateIsSelected && heureIsSlected && !dataLoading;
  const showSubmit = formEstComplete({
    dateIsSelected,
    heureIsSelected: heureIsSlected,
    aPeche,
    pointsEau,
    allPointsEaux,
  });

  const isPageLoading = load || dataLoading;

  return (
    <FermerEnregistrementFormBase
      texts={TEXTS}
      goTo={goTo}
      location={location}
      isLoading={isPageLoading}
      loadError={loadError}
      enregistrementEntete={enregistrementEntete}
      dataLoading={dataLoading}
      calendarRef={calendarRef}
      date={date}
      isOpenCalendar={isOpenCalendar}
      setIsOpenCalendar={setIsOpenCalendar}
      setdateIsSelected={setdateIsSelected}
      minSelectableDate={minSelectableDate}
      maxSelectableDate={maxSelectableDate}
      dateErrorKey={dateErrorKey}
      handleDateChange={handleDateChange}
      dateIsSelected={dateIsSelected}
      handleTimeChange={handleTimeChange}
    >
      {showFishingQuestion &&
        <OuiNonToggle
          texts={TEXTS}
          label={TEXTS.fishedQuestion}
          value={aPeche}
          onChange={handleAPecheChange}
        />
      }

      {aPeche === true && pointsEau.map((block, index) => {
        if (!shouldRenderPointEauBlock(block, index, allPointsEaux, pointsEau)) {
          return null;
        }

        return (
          <PointEauSection
            key={block.id}
            texts={TEXTS}
            index={index}
            block={block}
            isFirst={index === 0}
            showAutrePointEau={afficherAutrePointEau(index, pointsEau, allPointsEaux)}
            pointsEauxDisponibles={getPointsEauxDisponibles(allPointsEaux, pointsEau, index)}
            onPointEauChange={handlePointEauChange}
            onNombrePersonnesChange={handleNombrePersonnesChange}
            onNombreJoursChange={handleNombreJoursChange}
            onPrisesChange={handlePrisesChange}
            onCaptureDetailChange={handleCaptureDetailChange}
            onAutreEspeceChange={handleAutreEspeceChange}
            onEspeceChange={handleEspeceChange}
            onAutrePointEauChange={handleAutrePointEauChange}
          />
        );
      })}

      {showSubmit &&
        <>
          {aPeche === true &&
            <CaptureVerificationSommaire
              texts={TEXTS}
              mode="peche"
              blocks={pointsEau}
              allLocations={allPointsEaux}
            />
          }
          <div className="fec-submit-instruction">
            <span className="droit-acces-span">
              {TEXTS.submitInstruction}
            </span>
          </div>
          <button
            onClick={() => navigation()}
            className="fermer-enregistrement-button"
          >
            <span className="droit-acces-span">{TEXTS.closeButton}</span>
          </button>
        </>
      }
    </FermerEnregistrementFormBase>
  );
};

export default withNavigation(FermerEnregistrementEtCapture);
